// src/contexts/AuthContext.js
import { createContext, useState, useContext, useEffect, useCallback } from 'react';
import authService from '../services/authService';
import userManagementService from '../services/userManagementService';
import { useNotification } from './NotificationContext';
import { useLocale } from './LocaleContext';

const AuthContext = createContext(null);

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [passwordRecoveryMode, setPasswordRecoveryMode] = useState(false);
  const [loading, setLoading] = useState(true);

  const { showNotification } = useNotification();
  const { t } = useLocale();

  const logout = useCallback(async () => {
    const { error } = await authService.signOut();

    if (error) {
      showNotification(t('errors.unexpectedError'), 'error');
      throw new Error(error.message || 'Failed to logout');
    }

    setCurrentUser(null);
    setIsAuthenticated(false);
    localStorage.removeItem('supabase.auth.token');
  }, [showNotification, t]);

  const updateUserAndAuthStatus = useCallback(async (user) => {

    if (!user) {
      setCurrentUser(null);
      setIsAuthenticated(false);
      return;
    }

    let userProfile = null;
    try {
      const { data: profile, error: profileError } = await userManagementService.getCurrentUserProfile(user.id, user.email);


      if (profileError || !profile) {
        showNotification(t('errors.auth.userNotFoundOrCorrupt'), 'error');
        await logout();
        return;
      } else {
        userProfile = profile;
      }
    } catch (e) {
      showNotification(t('errors.unexpectedError'), 'error');
      await logout();
      return;
    }

    setCurrentUser({ ...user, ...userProfile, tournamentLimit: userProfile.tournament_limit });
    setIsAuthenticated(true);
  }, [logout, showNotification, t]);

  useEffect(() => {
    setLoading(true);
    const { data: { subscription: authListener } } = authService.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        setPasswordRecoveryMode(true);
      }
      setTimeout(async () => {
        if (session) {
          await updateUserAndAuthStatus(session.user);
        } else {
          setCurrentUser(null);
          setIsAuthenticated(false);
        }
        setLoading(false);
      }, 0);
    });

    return () => {
      authListener?.unsubscribe();
    };
  }, [updateUserAndAuthStatus]);

  const login = useCallback(async (email, password) => {
    const { data: loginData, error } = await authService.signInWithPassword({ email, password });
    const user = loginData?.user;

    if (error) {
      showNotification(t('errors.loginFailed'), 'error');
      throw new Error(error.message || 'ERROR_LOGIN_FAILED');
    }


    await updateUserAndAuthStatus(user);
    return { user };
  }, [updateUserAndAuthStatus, showNotification, t]);

  const signup = useCallback(async (email, password) => {

    const { data: user, error } = await authService.signUp({ email, password });


    if (error) {
      showNotification(t('errors.auth.signupFailed'), 'error');
      throw new Error(error.message);
    }
    return { user };
  }, [showNotification, t]);

  const value = {
    currentUser,
    isAuthenticated,
    loading,
    passwordRecoveryMode,
    setPasswordRecoveryMode,
    login,
    signup,
    logout,
    resetPasswordForEmail: authService.resetPasswordForEmail,
    updateUser: authService.updateUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};